// Vue
import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import pathify from '@/plugins/vuex-pathify'
import router from '@/router'

// Modules
import * as modules from './modules'
import {dispatch} from 'vuex-pathify'
import {encrypt} from '@/util/encryption';
import {checkError} from '@/util/error_handler';

Vue.use(Vuex)

const FRONTEND_AVAILABLE_PERMISSIONS = [
  'admin.usable',
  'catalogs.list.me',
  'catalogs.show.me',
  'commodities.list.me',
  'commodities.show.me',
  'news.list.me',
  'news.show.me',
  'pre_order_commodities.create.me',
  'pre_order_commodities.destroy.me',
  'pre_order_commodities.list.me',
  'pre_order_commodities.show.me',
  'pre_order_commodities.update.me',
];

// 一帳號多門市：記住上次選的門市，下次登入若仍屬於該帳號就直接進入
const ORGANIZATION_STORAGE_KEY = 'vue@organization_id';

const readStoredOrganizationId = () => {
  try {
    return parseInt(localStorage.getItem(ORGANIZATION_STORAGE_KEY), 10);
  } catch (e) {
    return NaN;
  }
};

const writeStoredOrganizationId = (id) => {
  try {
    localStorage.setItem(ORGANIZATION_STORAGE_KEY, String(id));
  } catch (e) {
    // ignore storage errors
  }
};

const store = new Vuex.Store({
  modules,
  plugins: [
    pathify.plugin,
  ],
  state: {
    isInit: false,
    userData: null,
    saltData: null,
    isAuthenticated: false,
    isLoading: false,
    loadingPercent: 0,
    token: null,
    myOrganization: null,
    myOrganizations: [],
    // 剛登入成功、還沒進首頁看過登入公告；只在這次登入的瞬間為 true，
    // 不持久化、重新整理頁面也不會殘留，首頁看完（或沒有公告內容）就消費掉。
    justLoggedIn: false,
  },
  mutations: {
    setInit(state, payload) {
      state.isInit = payload
    },
    setJustLoggedIn(state, payload) {
      state.justLoggedIn = payload
    },
    setUser(state, payload) {
      state.userData = payload
    },
    setToken(state, token) {
      state.token = token
    },
    setSalt(state, payload) {
      state.saltData = payload
    },
    setMyOrganization(state, payload) {
      state.myOrganization = payload
    },
    setMyOrganizations(state, payload) {
      state.myOrganizations = payload
    },
    setIsAuthenticated(state, payload) {
      state.isAuthenticated = payload
    },
    setIsLoading(state, payload) {
      state.isLoading = payload
      state.loadingPercent = ''
    },
    setLoadingPercent(state, payload) {
      state.loadingPercent = payload
    },
  },
  actions: {
    init: async ({commit, state}) => {
      dispatch('load');
      const user = state.userData
      const isLogin = await dispatch('check_token');
      if (user === null || isLogin === false) {
        commit('setInit', true)
        dispatch('clear')
        return
      }
      let available = await dispatch('getPermissions', {needLogin: true});
      available = available && await dispatch('initMyOrganization', {needLogin: true});
      if (available === false) {
        commit('setInit', true)
        dispatch('clear')
        return
      }
      commit('setIsAuthenticated', true);
      commit('setInit', true)
      // dispatch('permission/init', null, {root: true})
    },
    save: ({state}) => {
      localStorage.setItem('vue@user', JSON.stringify({
        user: state.userData,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }))
    },
    load: ({commit, state}) => {
      let data = localStorage.getItem('vue@user') || '{}'
      data = JSON.parse(data);
      if (data.user !== undefined) commit('setUser', data.user);
      if (data.token !== undefined) commit('setToken', data.token);
      if (data.isAuthenticated !== undefined) commit('setIsAuthenticated', data.isAuthenticated);
    },
    clear: ({commit, dispatch}) => {
      localStorage.removeItem('vue@user')
      commit('setIsAuthenticated', false);
      commit('setUser', {});
      commit('setToken', '');
      commit('setMyOrganization', {});
      commit('setMyOrganizations', []);
      dispatch('permission/clear', null, {root: true})
    },
    async check_token({state}) {
      try {
        const token = state.token;
        const url = process.env.VUE_APP_APIPATH
        const headers = {Authorization: token}
        const response = await axios.get(`${url}auth/check_token`, {headers})
        if (response.status === 404) {
          return true;
        } else {
          return checkError(undefined, response);
        }
      } catch (e) {
        console.error(e)
        return false;
      }
    },
    async sign_in_with_email({commit, state, dispatch}, {email, password, token}) {
      // console.log('sign_in_with_email');
      const encrypted = encrypt(password, state.saltData.key, state.saltData.salt);

      const response = await axios.post(`${process.env.VUE_APP_APIPATH}auth/sign_in_with_email`, {
        email: email,
        salt_id: state.saltData.id,
        encrypted: encrypted,
        token: token,
      });
      const authorization = response.headers.authorization;
      commit('setToken', authorization);
      commit('setUser', response.data)
      commit('setIsAuthenticated', true)
      dispatch('save')
      return response;
    },
    async getPermissions({commit, dispatch, getters, state}, {needLogin}) {
      try {
        dispatch('showLoading')
        const token = state.token;
        const url = process.env.VUE_APP_APIPATH
        axios.defaults.headers.common.Authorization = token;
        axios.defaults.headers.common.version = '10.0.0';
        const headers = {Authorization: token}
        const response = await axios.get(`${url}admin/get_permissions`, {headers})
        let available = false;
        try {
          const permissions = response.data.map(permission => permission.name)
          dispatch('permission/update', permissions, {root: true})
          available = getters.hasPermission(FRONTEND_AVAILABLE_PERMISSIONS)
        } catch (e) {
          console.error(e);
        }
        dispatch('hideLoading')
        if (!available) {
          if (needLogin) {
            router.push({path: '/login'});
          } else {
            alert('你沒有權限')
          }
        }
        return available;
      } catch (e) {
        dispatch('hideLoading')
        console.error(e)
      }
      return false;
    },
    async userSignOut({commit}) {
      try {
        commit('setUser', null)
        commit('setToken', null)
        commit('setSalt', null)
        commit('setIsAuthenticated', false)
        dispatch('permission/clear', null, {root: true})
        dispatch('clear')
        router.push({name: 'login'});
      } catch (e) {
        dispatch('hideLoading')
        console.error(e)
      }
    },
    showLoading({commit}) {
      commit('setIsLoading', true)
    },
    hideLoading({commit}) {
      commit('setIsLoading', false)
    },
    setLoadingPercent({commit}, {percent}) {
      commit('setLoadingPercent', percent)
    },
    async initSalt({state, commit, dispatch}) {
      try {
        if (state.hasSalt) return;
        dispatch('showLoading')
        const url = process.env.VUE_APP_APIPATH
        const result = await axios.get(`${url}auth/generate_salt`);
        dispatch('hideLoading')
        commit('setSalt', result.data);
      } catch (e) {
        dispatch('hideLoading')
        console.error(e)
        alert(e);
      }
    },
    async updateSalt({commit}) {
      const url = process.env.VUE_APP_APIPATH
      const result = await axios.get(`${url}auth/generate_salt`);
      commit('setSalt', result.data);
      return result.data;
    },
    async initMyOrganization({commit, dispatch}, {needLogin}) {
      const url = process.env.VUE_APP_APIPATH
      const result = await axios.get(`${url}model/my_organization`);
      console.log('my organization (' + result.data + ")");
      if (result.data === null || result.data === undefined || result.data.length === 0) {
        if (needLogin) router.push({path: '/login'});
        dispatch('error/show', {msg: "找不到您的廠商資料，請與官方人員聯繫"}, {root: true});
        return false;
      }
      const orgs = Array.isArray(result.data) ? result.data : [result.data];
      commit('setMyOrganizations', orgs);
      // 只有一間門市直接進入；多間門市時，上次選的仍有效就沿用，否則等使用者在選擇頁決定
      const storedId = readStoredOrganizationId();
      const remembered = orgs.find(o => o.id === storedId);
      const current = orgs.length === 1 ? orgs[0] : (remembered || null);
      commit('setMyOrganization', current);
      if (current) writeStoredOrganizationId(current.id);
      // 上次選的門市已經不在名單（被後台移除），剩下唯一一間：直接進入，並說明發生了什麼
      if (current && !Number.isNaN(storedId) && storedId !== current.id) {
        dispatch('error/show', {title: '提醒', msg: `您的門市已被移除，已切換到「${current.name || current.serial_number}」`}, ROOT_DISPATCH);
      }
      return true;
    },
    // 選擇／切換門市：記住選擇並整頁重載，讓型錄、訂單、收藏等資料全部重新以該門市載入
    selectOrganization({commit}, {organization, reload = false}) {
      commit('setMyOrganization', organization);
      writeStoredOrganizationId(organization.id);
      if (reload) window.location.assign(process.env.BASE_URL || '/');
    },
    async getAuthHeader({state}) {
      let token = state.token;
      // fallback to localStorage saved user/session (compat with persistence)
      if (!token) {
        try {
          const saved = localStorage.getItem('vue@user');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed && parsed.token) token = parsed.token;
          }
        } catch (e) {
          // ignore parse errors
        }
      }
      if (!token) return {};
      return {
        Authorization: String(token).startsWith('Bearer ') ? String(token) : `Bearer ${String(token)}`
      };
    },
    hasPermission: ({state}, {permissions}) => {
      console.log('hasPermission', permissions);
      // console.log('\t|_ ', state.permission.permissions);
      for (let i = 0; i < permissions.length; i++) {
        if (state.permission.permissions.indexOf(permissions[i]) >= 0) {
          console.log('\t|_ true');
          return true;
        }
      }
      console.log('\t|_ false');
      return false
    },
  },
  getters: {
    isAuthenticated(state) {
      return state.user !== null && state.user !== undefined
    },
    isInit(state) {
      return state.isInit === true
    },
    hasPermission: (state) => (permissions) => {
      for (let i = 0; i < permissions.length; i++) {
        if (state.permission.permissions.indexOf(permissions[i]) >= 0) return true
      }
      return false
    },
    hasSalt(state) {
      return state.saltData !== null && state.saltData !== undefined
    },
    getPermissions(state) {
      return state.permission.permissions;
    },
  },
})

store.subscribe(mutation => {
  if (mutation.type.startsWith('user/')) {
    store.dispatch('user/update', mutation)
  }
})

store.dispatch('init')

axios.interceptors.request.use(config => {
  const organization = store.state.myOrganization;
  if (organization && organization.id) {
    config.headers = config.headers || {};
    config.headers['X-Organization-Id'] = organization.id;
  }
  return config;
});

axios.interceptors.response.use(response => {
  const newToken = response.headers.authorization;
  console.log('[interceptor] url:', response.config.url, '| authorization header:', newToken || '(none)');
  if (newToken && newToken !== store.state.token) {
    console.log('[interceptor] token updated');
    store.commit('setToken', newToken);
    store.dispatch('save');
  }
  return response;
}, error => {
  // 帶了門市卻被拒絕（例如後台已把帳號從該門市移除）：清掉目前門市，回到選擇頁重新確認
  const config = error && error.config;
  const code = error && error.response && error.response.data && error.response.data.error && error.response.data.error.code;
  if (error && error.response && error.response.status === 403 && code === 'unauthorized' && config && config.headers
    && config.headers['X-Organization-Id'] && store.state.myOrganization) {
    store.commit('setMyOrganization', null);
    if (router.currentRoute.name !== 'select-organization') router.push({name: 'select-organization'});
  }
  return Promise.reject(error);
});

export default store

export const ROOT_DISPATCH = Object.freeze({root: true})
